import {
    FilterCriteria,
    Priority,
    VendorProject,
    VendorTask,
    VendorTaskStatus,
} from './types'

/**
 * Strip a date-time string down to YYYY-MM-DD. The backend returns dates as
 * either 'YYYY-MM-DD' or 'YYYY-MM-DDT00:00:00Z' depending on the endpoint, so
 * we always normalize before comparing or sending.
 */
export const safeDateOnly = (s?: string | null): string | null => {
    if (!s) return null
    return s.split('T')[0]
}

const PRIORITY_WEIGHT: Record<Priority, number> = {
    URGENT: 0,
    HIGH: 1,
    MEDIUM: 2,
    LOW: 3,
}

export interface TaskTransitionAction {
    label: string
    toStatus: VendorTaskStatus
    kind: 'positive' | 'negative' | 'neutral'
}

/**
 * State machine from §5 of MY_PROJECTS_FEATURE_GUIDE.md.
 */
export const getTaskTransitions = (
    task: VendorTask,
): TaskTransitionAction[] => {
    switch (task.status) {
        case 'ASSIGNED':
            return [
                { label: 'Start', toStatus: 'IN_PROGRESS', kind: 'positive' },
            ]
        case 'IN_PROGRESS':
            return [
                { label: 'Complete', toStatus: 'COMPLETED', kind: 'positive' },
                { label: 'Pause', toStatus: 'ON_HOLD', kind: 'neutral' },
                { label: 'Reset', toStatus: 'ASSIGNED', kind: 'negative' },
            ]
        case 'ON_HOLD':
            return [
                { label: 'Resume', toStatus: 'IN_PROGRESS', kind: 'positive' },
            ]
        case 'COMPLETED':
        default:
            return []
    }
}

/**
 * Recompute embedded counters whenever the task list changes. Returns a brand
 * new project object so callers can keep the "always replace" invariant.
 */
export const recomputeTaskCounts = (project: VendorProject): VendorProject => {
    const tasks = project.tasks ?? []
    let completed = 0
    let inProgress = 0
    tasks.forEach((t) => {
        if (t.status === 'COMPLETED') completed++
        else if (t.status === 'IN_PROGRESS') inProgress++
    })
    return {
        ...project,
        taskCount: tasks.length,
        completedTaskCount: completed,
        inProgressTaskCount: inProgress,
    }
}

/** Filter logic from §7.1. */
export const applyProjectFilter = (
    projects: VendorProject[],
    criteria: FilterCriteria,
): VendorProject[] => {
    const dateFrom = safeDateOnly(criteria.dateFrom ?? null)
    const dateTo = safeDateOnly(criteria.dateTo ?? null)
    const dateFilterActive = !!dateFrom || !!dateTo

    return projects.filter((p) => {
        if (dateFilterActive) {
            const start = safeDateOnly(p.start_date)
            const end = safeDateOnly(p.end_date)
            if (!start || !end) return false
            if (dateFrom && start < dateFrom) return false
            if (dateTo && end > dateTo) return false
        }

        if (criteria.priority && criteria.priority.length > 0) {
            if (!criteria.priority.includes(p.priority)) return false
        }

        if (criteria.status && criteria.status.length > 0) {
            const tasks = p.tasks ?? []
            const hasMatch = tasks.some((t) =>
                (criteria.status as VendorTaskStatus[]).includes(t.status),
            )
            if (!hasMatch) return false
        }

        return true
    })
}

export const todayYYYYMMDD = (): string => {
    const d = new Date()
    const m = `${d.getMonth() + 1}`.padStart(2, '0')
    const day = `${d.getDate()}`.padStart(2, '0')
    return `${d.getFullYear()}-${m}-${day}`
}

export type VendorProjectBucket =
    | 'active'
    | 'overdue'
    | 'upcoming'
    | 'completed'
    | 'no_dates'

export const VENDOR_PROJECT_BUCKET_COLORS: Record<
    VendorProjectBucket,
    { fg: string; bg: string; text: string; label: string }
> = {
    active: {
        fg: '#1976d2',
        bg: '#e3f2fd',
        text: '#0d47a1',
        label: 'Active',
    },
    overdue: {
        fg: '#d32f2f',
        bg: '#ffebee',
        text: '#b71c1c',
        label: 'Overdue',
    },
    upcoming: {
        fg: '#7b1fa2',
        bg: '#f3e5f5',
        text: '#4a148c',
        label: 'Upcoming',
    },
    completed: {
        fg: '#2e7d32',
        bg: '#e8f5e9',
        text: '#1b5e20',
        label: 'Completed',
    },
    no_dates: {
        fg: '#616161',
        bg: '#f5f5f5',
        text: '#212121',
        label: 'No Dates',
    },
}

const BUCKET_TO_RANK: Record<VendorProjectBucket, 1 | 2 | 3 | 4 | 5> = {
    active: 1,
    overdue: 2,
    upcoming: 3,
    completed: 4,
    no_dates: 5,
}

/**
 * A project counts as "no outstanding work" when either it has no tasks at
 * all or every task it does have is in the COMPLETED state. We only treat
 * past projects this way - if the deadline hasn't passed, this project is
 * still considered active so the user can see they're ahead of schedule.
 *
 * The empty-task case matters because a project past its end_date with no
 * tasks shouldn't be flagged as overdue (there's literally nothing left to
 * do), so we bucket it as "completed" instead.
 */
const isProjectFullyCompleted = (project: VendorProject): boolean => {
    const tasks = project.tasks ?? []
    if (tasks.length === 0) return true
    return tasks.every((t) => t.status === 'COMPLETED')
}

export const getProjectBucket = (
    project: VendorProject,
    todayStr: string = todayYYYYMMDD(),
): VendorProjectBucket => {
    const start = safeDateOnly(project.start_date)
    const end = safeDateOnly(project.end_date)

    if (!start || !end) return 'no_dates'
    if (start <= todayStr && todayStr <= end) return 'active'
    if (end < todayStr) {
        // Past the deadline - only "overdue" if there's still unfinished work,
        // otherwise the project is just completed.
        return isProjectFullyCompleted(project) ? 'completed' : 'overdue'
    }
    if (start > todayStr) return 'upcoming'
    return 'no_dates'
}

const getProjectStatusBucket = (
    project: VendorProject,
    todayStr: string,
): 1 | 2 | 3 | 4 | 5 => BUCKET_TO_RANK[getProjectBucket(project, todayStr)]

const completionPercent = (project: VendorProject): number => {
    const tasks = project.tasks ?? []
    if (tasks.length === 0) return 0
    const done = tasks.filter((t) => t.status === 'COMPLETED').length
    return done / tasks.length
}

/** Smart default sort from §7.2. */
export const smartSortProjects = (
    projects: VendorProject[],
): VendorProject[] => {
    const today = todayYYYYMMDD()
    const copy = [...projects]
    copy.sort((a, b) => {
        // 1. status bucket
        const aBucket = getProjectStatusBucket(a, today)
        const bBucket = getProjectStatusBucket(b, today)
        if (aBucket !== bBucket) return aBucket - bBucket

        // 2. priority weight
        const pw = PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority]
        if (pw !== 0) return pw

        // 3. completion percentage ascending (>5% diff only)
        const aPct = completionPercent(a)
        const bPct = completionPercent(b)
        if (Math.abs(aPct - bPct) > 0.05) return aPct - bPct

        // 4. start date ascending
        const aStart = safeDateOnly(a.start_date) ?? '9999-12-31'
        const bStart = safeDateOnly(b.start_date) ?? '9999-12-31'
        if (aStart !== bStart) return aStart < bStart ? -1 : 1

        // 5. title alpha
        return a.title.localeCompare(b.title)
    })
    return copy
}

const SORT_FIELD_GETTERS: Record<
    string,
    (p: VendorProject) => string | number
> = {
    title: (p) => p.title.toLowerCase(),
    priority: (p) => PRIORITY_WEIGHT[p.priority],
    start_date: (p) => safeDateOnly(p.start_date) ?? '9999-12-31',
    end_date: (p) => safeDateOnly(p.end_date) ?? '9999-12-31',
    created_at: (p) => safeDateOnly(p.created_date) ?? '',
    updated_at: (p) => safeDateOnly(p.modified_date) ?? '',
    assigned_by_name: (p) => p.assigned_by?.name_field?.toLowerCase() ?? '',
    assigned_to_name: (p) => p.assigned_by?.name_field?.toLowerCase() ?? '',
}

export const explicitSortProjects = (
    projects: VendorProject[],
    sortBy: string,
    sortOrder: 'asc' | 'desc',
): VendorProject[] => {
    const getter = SORT_FIELD_GETTERS[sortBy]
    if (!getter) return projects
    const dir = sortOrder === 'desc' ? -1 : 1
    const copy = [...projects]
    copy.sort((a, b) => {
        const av = getter(a)
        const bv = getter(b)
        if (av < bv) return -1 * dir
        if (av > bv) return 1 * dir
        return 0
    })
    return copy
}

/**
 * Days past due indicator, suitable for VendorProjectCard. Negative or zero
 * values mean "not overdue".
 */
export const getDaysPastDue = (project: VendorProject): number => {
    const end = safeDateOnly(project.end_date)
    if (!end) return 0
    // Past the deadline but every task done → not actually overdue.
    if (isProjectFullyCompleted(project)) return 0
    const today = new Date(todayYYYYMMDD())
    const endDate = new Date(end)
    const diffMs = today.getTime() - endDate.getTime()
    return Math.floor(diffMs / (1000 * 60 * 60 * 24))
}

export const isProjectOverdue = (project: VendorProject): boolean => {
    return getDaysPastDue(project) > 0
}

/**
 * Replace a project in a project list using object identity rules from §4.2.
 * Returns a brand new array.
 */
export const upsertProject = (
    list: VendorProject[],
    updated: VendorProject,
): VendorProject[] => {
    let found = false
    const next = list.map((p) => {
        if (p.id === updated.id) {
            found = true
            return { ...p, ...updated, tasks: updated.tasks ?? p.tasks }
        }
        return p
    })
    if (!found) next.push(updated)
    return next
}

export const removeProject = (
    list: VendorProject[],
    id: number,
): VendorProject[] => list.filter((p) => p.id !== id)

/**
 * Apply a task-level mutation across the project list, recomputing counts on
 * the affected project. The mutator can return:
 *   - a new task array (for create/update)
 *   - null to indicate "delete this task"
 */
export const mutateProjectTasks = (
    list: VendorProject[],
    projectId: number,
    mutator: (tasks: VendorTask[]) => VendorTask[],
): VendorProject[] => {
    return list.map((p) => {
        if (p.id !== projectId) return p
        const nextTasks = mutator(p.tasks ?? [])
        return recomputeTaskCounts({ ...p, tasks: nextTasks })
    })
}
